<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || (!tpaListTableData.length && !showLoader && !applyErrorState)"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- *********************************************
        *												 *
        *                      Content                   *
        *												 *
        ********************************************** -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState && !!tpaListTableData.length" class="content-wrapper prevent-auto-margins">
                <!-- Table Generic -->
                <table-generic
                :headerData="tpaListHeaderData"
                :tableStyles="tpaListTableStyles"
                :tableData="tpaListTableData"
                :hasMore="getTpaListHasMore"
                :isLoadingMore="tableLoadingMore"
                :loadingMoreText="t('companyPayments.tpaRequestsList.tableLoadingMore')"
                @loadMore="loadMoreHandler"
                @rowClicked="tpaListClickHandler($event)"/>
            </div>
        </transition>
        <template #empty-state-slot>
            <!-- Empty State -->
            <empty-state-generic
            v-if="!tpaListTableData.length && !showLoader && !applyErrorState"
            illustration="emptyState/companyPayments-tpa-requests"
            :title="t('companyPayments.tpaRequestsList.emptyState.title')"
            :description="t('companyPayments.tpaRequestsList.emptyState.description')"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Detail Modal -->
    <modal-detail-mdw-line-group
    v-if="showDetailModal"
    forceNegativeAmount
    subTitleIcon="arrow-down-full"
    :headerColor="headerColor"
    :icon="headerBigIcon"
    :title="detailInfo.title"
    :subTitle="detailSubTitle"
    :requestPayload="payloadDetailModal"
    :detailRequestAction="requestAction"
    :errorStateMessage="t('companyPayments.tpaRequestsList.errorStateMessage')"
    :statusIconSvgName="detailInfo.status.icon"
    :statusIconColor="detailInfo.status.classes"
    @hideMe="showDetailModal = false"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import ModalDetailMdwLineGroup from '@/components/generic/modals/side/DetailMdwLineGroup'

/* Assets & Helpers */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { ref, computed, onBeforeUnmount } from 'vue'
import { getDate } from '@/assets/scripts/dateBuilders'
import { iconMapper, iconColorMapper, colorStatusMapperModal, iconModalMapper } from '@/assets/scripts/mappers/tpa'
import { amountFormat } from '@/assets/scripts/amounts'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { getListTPAsRequested, clearFeatureStates } = mapActions(moduleMapper.COMPANY_PAYMENTS_TPA_REQUESTS_LIST)
const { getTpaList, getTpaListHasMore } = mapGetters(moduleMapper.COMPANY_PAYMENTS_TPA_REQUESTS_LIST)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const tableLoadingMore = ref(false)
const selectedTpaDetail = ref(false)

/*************************************************
*                                                *
*                Side Modal Info                 *
*                                                *
*************************************************/
const showDetailModal = ref(false)
const requestAction = `${moduleMapper.COMPANY_PAYMENTS_TPA_REQUESTS_LIST}/getOperationGenericDetail`
const payloadDetailModal = ref({})

const detailInfo = ref({})

const headerColor = computed(() => {
    return colorStatusMapperModal(selectedTpaDetail.value.status.name)
})

const headerBigIcon = computed(() => {
    return iconModalMapper(selectedTpaDetail.value.status.name)
})

const detailSubTitle = computed(() => {
    return amountFormat(Math.abs(selectedTpaDetail.value.total), selectedTpaDetail.value.currency)
})

/*************************************************
*                                                *
*              Tpa List Table Data               *
*                                                *
*************************************************/
const tpaListHeaderData = [
    { label: t('companyPayments.tpaRequestsList.table.tableHeader.establishmentName'), showInMobile: { groupWith: ['total', 'creationDate'] }, dataAttr: 'establishmentName', rowCellType: 'title' },
    { label: t('companyPayments.tpaRequestsList.table.tableHeader.quantity'), dataAttr: 'quantity', rowCellType: 'description' },
    { label: t('companyPayments.tpaRequestsList.table.tableHeader.id'), dataAttr: 'id', rowCellType: 'description' },
    { label: t('companyPayments.tpaRequestsList.table.tableHeader.creationDate'), dataAttr: 'creationDate', rowCellType: 'date' },
    { label: t('companyPayments.tpaRequestsList.table.tableHeader.channel'), dataAttr: 'channel', rowCellType: 'description' },
    { label: t('companyPayments.tpaRequestsList.table.tableHeader.status'), showInMobile: true, dataAttr: 'status', rowCellType: 'status' }
]

const tpaListTableStyles = [
    { width: '20%', 'min-width': '20%' },
    { width: '150px', 'min-width': '150px' },
    { width: '150px', 'min-width': '150px' },
    null,
    null,
    null
]

const tpaListTableData = computed(() => {
    return getTpaList.value.map(tpa => {
        return [
            { value: tpa.id, dataAttr: 'id' },
            { type: 'title', value: tpa.establishmentName, dataAttr: 'establishmentName' },
            { value: tpa.quantity, dataAttr: 'quantity' },
            { value: tpa.id, dataAttr: 'requestId' },
            {
                value: {
                    content: getMobileAppState.value
                            ? `${tpa.quantity} TPA - ${getDate(tpa.creationDate)}`
                            : getDate(tpa.creationDate),
                    classes: getMobileAppState.value ? 'capitalize small secondary-color' : 'capitalize'
                },
                dataAttr: 'creationDate'
            },
            { value: tpa.channel.description, dataAttr: 'channel' },
            {
                type: 'status',
                value: {
                    text: tpa.status.value || '',
                    icon: iconMapper(tpa.status.name),
                    classes: iconColorMapper(tpa.status.name)
                },
                dataAttr: 'status'
            }
        ]
    })
})

const loadMoreHandler = () => {
    paginationParams.value.pageNumber++
    requestTpaList()
}

const tpaListClickHandler = row => {
    selectedTpaDetail.value = getTpaList.value.find(tpa => tpa.id === row.rowId)

    detailInfo.value = {
        id: row.rowId,
        title: selectedTpaDetail.value.establishmentName,
        amount: selectedTpaDetail.value.total,
        status: row.rowData.find(data => data.type === 'status').value
    }

    payloadDetailModal.value = {
        operationId: selectedTpaDetail.value.operationId
    }
    showDetailModal.value = true
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const paginationParams = ref({
    pageNumber: 1,
    pageSize: 20
})

const requestTpaList = async () => {
    applyErrorState.value = false
    showFeedbackToast.value = false

    paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true

    const tpaListPayload = {
        ...paginationParams.value
	}

    try {
        await getListTPAsRequested(tpaListPayload)
        showLoader.value = false
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
		applyErrorState.value = true
        tableLoadingMore.value = false
		errorHandler(error, requestTpaList)
    }
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

/*************************************************
*                                                *
*             Success/Error Handler              *
*                                                *
*************************************************/
const errorHandler = (response, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false
    tableLoadingMore.value = false

	feedbackToastData.value = errorObjectConstructor(response, retryFn)
	showFeedbackToast.value = true
}

requestTpaList()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.content-wrapper {
    @include commonEnteringAnimation;
    width: 100%;
}
</style>
